import { useCallback, useMemo } from 'react';
import { cn } from '@ais-ui/utils';
import {
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import {
  LayerItem,
  useLayers,
  getCombinedExtent,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { PiCaretDown, PiCaretRight, PiFolderOpen } from 'react-icons/pi';
import { ChildLayerRow } from './ChildLayerRow';
import { LayerRow } from '../LayerRow';
import { TrackTimeControl } from './TrackTimeControl';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';

interface NestedLayerRowProps {
  layer: LayerItem;
  className?: string;
}

type CheckedState = boolean | 'indeterminate';

/**
 * A collapsible parent row for a layer with children (e.g., feature collections).
 * Uses the shared LayerRow layout to stay aligned with flat layer rows.
 */
export const NestedLayerRow = ({ layer, className }: NestedLayerRowProps) => {
  const {
    toggleChildrenVisibility,
    toggleLayerExpanded,
    removeLayerWithChildren,
    zoomToLayer,
    zoomToExtent,
  } = useLayers();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const childLayers = useMemo(() => layer.children ?? [], [layer.children]);

  const checkedState: CheckedState = useMemo(() => {
    if (childLayers.length === 0) return false;
    const visibleCount = childLayers.filter((l) => l.isVisible).length;
    if (visibleCount === 0) return false;
    if (visibleCount === childLayers.length) return true;
    return 'indeterminate';
  }, [childLayers]);

  const handleToggleVisibility = useCallback(
    (value: boolean | 'indeterminate') => {
      const newVisible = value !== false;
      toggleChildrenVisibility(layer.id, newVisible);
    },
    [layer.id, toggleChildrenVisibility],
  );

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleLayerExpanded(layer.id);
    },
    [layer.id, toggleLayerExpanded],
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      removeLayerWithChildren(layer.id);
    },
    [layer.id, removeLayerWithChildren],
  );

  const handleFlyToGroup = useCallback(() => {
    const extent = getCombinedExtent(
      layer.children ?? [],
      displayProjection,
      dataProjection,
    );
    if (extent) {
      zoomToExtent(extent);
    }
  }, [layer.children, zoomToExtent, displayProjection, dataProjection]);

  const handleFlyToChild = useCallback(
    (childLayer: LayerItem) => {
      if (childLayer.layer instanceof VectorLayer) {
        zoomToLayer(childLayer.layer as VectorLayer<Feature<OlGeometry>>);
      }
    },
    [zoomToLayer],
  );

  return (
    <div
      className={cn(
        'rounded-lg transition-all duration-200',
        layer.isExpanded &&
          'has-[[data-parent-row]:hover]:bg-muted has-[[data-parent-row]:hover]:shadow-sm',
      )}
    >
      {/* Parent row — uses shared LayerRow for consistent alignment */}
      <LayerRow
        data-parent-row
        className={cn(
          'group/row',
          layer.isExpanded && 'hover:bg-transparent hover:shadow-none',
          className,
        )}
      >
        <LayerRow.Leading>
          <div className="-ml-1.5 flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleToggleExpand}
              className={cn(
                'hover:bg-accent rounded inline-flex items-center justify-center',
                layer.isExpanded
                  ? 'p-0.5'
                  : 'w-0 p-0 opacity-0 overflow-hidden group-hover/row:w-5 group-hover/row:p-0.5 group-hover/row:opacity-100 transition-[width,opacity,padding] duration-150 ease-out',
              )}
            >
              {layer.isExpanded ? (
                <PiCaretDown className="size-3.5" />
              ) : (
                <PiCaretRight className="size-3.5" />
              )}
            </button>
            <Checkbox
              checked={checkedState}
              onCheckedChange={handleToggleVisibility}
            />
          </div>
          <div className="flex items-center space-x-2 min-w-0">
            <PiFolderOpen className="size-4 flex-shrink-0" />
            <div className="min-w-0">
              <button
                type="button"
                className="text-sm font-medium truncate text-left cursor-pointer hover:underline block"
                onClick={handleFlyToGroup}
              >
                {layer.name}
              </button>
              {layer.metadata && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-[10px] text-muted-foreground truncate block cursor-default">
                      {layer.metadata}
                    </span>
                  </TooltipTrigger>
                  {layer.properties?.lastUpdatedAt && (
                    <TooltipContent>
                      Updated{' '}
                      {formatDateTimeWithTimeZone(
                        new Date(layer.properties.lastUpdatedAt as string),
                      )}
                    </TooltipContent>
                  )}
                </Tooltip>
              )}
            </div>
          </div>
        </LayerRow.Leading>
        <LayerRow.Trailing>
          {layer.properties?.timeControl && (
            <TrackTimeControl
              layerId={layer.id}
              currentHours={layer.properties.timeControlHours}
            />
          )}
          <LayerRow.ZoomButton
            onClick={(e) => {
              e.stopPropagation();
              handleFlyToGroup();
            }}
            tooltip="Zoom to group"
          />
          <LayerRow.RemoveButton
            onClick={handleRemove}
            tooltip="Remove layer group"
          />
        </LayerRow.Trailing>
      </LayerRow>

      {/* Children (expanded) — pl-2.5 aligns tree vertical line with chevron center */}
      {layer.isExpanded && childLayers.length > 0 && (
        <div className="pl-2.5">
          {childLayers.map((child, index) => (
            <ChildLayerRow
              key={child.id}
              layer={child}
              isFirst={index === 0}
              isLast={index === childLayers.length - 1}
              onFlyTo={handleFlyToChild}
            />
          ))}
        </div>
      )}
    </div>
  );
};
